import { useState } from 'react';
import { Headphones, Radio, Search, Square, Volume2, VolumeX } from 'lucide-react';
import type { SimState } from '../domain/model';
import { callsign, radioKindLabels, tacticalStatus } from '../domain/radio';
import type { RadioController } from '../audio/useRadioAudio';
import { voiceAvailability } from '../audio/radioVoices';
import type { Perform } from './ResourceManager';
import { time } from '../format';

export function RadioPlaybackStatus({ audio }: { audio: RadioController }) {
  const { preferences: p, runtime: r } = audio;
  const status = !p.enabled
    ? 'Funk stummgeschaltet'
    : !p.volume
      ? 'Lautstärke auf 0 %'
      : !p.speech
        ? 'Sprachausgabe ausgeschaltet'
        : !r.unlocked
          ? 'Ton noch nicht freigegeben'
          : r.speaking
            ? r.currentVoice
              ? 'Spricht: ' + r.currentVoice
              : 'Sprachaufnahme wird geladen …'
            : 'Sprachausgabe bereit';
  return (
    <section className="radio-playback-status" aria-label="Audioausgabe">
      <div>
        <b role="status">{status}</b>
        <small>
          Ton nach dem Neuladen einmal freigeben. Der Stimmtest aktiviert neuronale Sprache.
        </small>
      </div>
      <button className="button primary" onClick={() => void audio.test()}>
        Stimme aktivieren & testen
      </button>
      {p.playback === 'direct' ? (
        <p className="radio-help">
          Direkter Audioplayer unten:{' '}
          {r.directPhase === 'playing'
            ? 'läuft · ' + r.directSeconds.toFixed(1) + ' Sekunden'
            : r.directPhase === 'blocked'
              ? 'Bitte ▶ drücken'
              : 'bereit'}
        </p>
      ) : (
        <label className="radio-output-meter">
          Audiopegel
          <meter
            aria-label="Audiopegel der neuronalen Stimme"
            min="0"
            max="1"
            value={r.outputLevel}
          />
          <output>{Math.round(r.outputLevel * 100)} %</output>
        </label>
      )}
      {r.error && (
        <p className="form-error" role="alert">
          {r.error}
        </p>
      )}
    </section>
  );
}

export function RadioAudioControls({ audio }: { audio: RadioController }) {
  const { preferences: p, runtime, voices } = audio;
  const neural = p.speechProvider === 'auto' && runtime.neuralAvailable;
  return (
    <section className="radio-audio-controls">
      <div className="radio-section-heading">
        <span>
          <Headphones size={18} />
          <b>Ton & Sprachausgabe</b>
        </span>
        <button
          className="button secondary"
          onClick={() =>
            p.enabled && runtime.unlocked ? audio.change({ enabled: false }) : void audio.activate()
          }
        >
          {p.enabled && runtime.unlocked ? <VolumeX size={16} /> : <Volume2 size={16} />}
          {p.enabled && runtime.unlocked ? 'Stummschalten' : 'Funk aktivieren'}
        </button>
      </div>
      <div className="radio-preferences-grid">
        <label className="radio-voice">
          Audioausgang
          <select
            aria-label="Audioausgang"
            value={p.playback}
            onChange={(e) => audio.change({ playback: e.target.value as 'direct' | 'filtered' })}
          >
            <option value="direct">Direktton · empfohlen für Safari</option>
            <option value="filtered">Web Audio · mit Funkfilter</option>
          </select>
        </label>
        <label className="switch-row">
          <span>
            Ereignissignale<small>Notruf, Melderalarm, Status und Lageänderung</small>
          </span>
          <input
            type="checkbox"
            checked={p.effects}
            onChange={(e) => audio.change({ effects: e.target.checked })}
          />
        </label>
        <label className="switch-row">
          <span>
            Sprachausgabe<small>Deutsche Stimme für simulierte Funkmeldungen</small>
          </span>
          <input
            type="checkbox"
            checked={p.speech}
            onChange={(e) => audio.change({ speech: e.target.checked })}
          />
        </label>
        <label className="switch-row">
          <span>
            Statusmeldungen vorlesen<small>Zusätzliche Ansage der digitalen Statuswechsel</small>
          </span>
          <input
            type="checkbox"
            checked={p.speakStatuses}
            disabled={!p.speech}
            onChange={(e) => audio.change({ speakStatuses: e.target.checked })}
          />
        </label>
        <label className="switch-row">
          <span>
            Funksequenzen
            <small>Sprechtaste, kurzes „Tschh“, Funkfilter und klarer Abschlusston</small>
          </span>
          <input
            type="checkbox"
            checked={p.radioNoise}
            onChange={(e) => audio.change({ radioNoise: e.target.checked })}
          />
        </label>
        <label className="radio-voice">
          Sprachmodell
          <select
            aria-label="Sprachmodell"
            value={p.speechProvider}
            onChange={(e) => audio.change({ speechProvider: e.target.value as 'auto' | 'browser' })}
          >
            <option value="auto">Neuronale Stimmen (empfohlen)</option>
            <option value="browser">Browser-/Systemstimmen</option>
          </select>
        </label>
        <label className="radio-range">
          Lautstärke <output>{Math.round(p.volume * 100)} %</output>
          <input
            aria-label="Funklautstärke"
            type="range"
            min="0"
            max="100"
            value={Math.round(p.volume * 100)}
            onChange={(e) => audio.change({ volume: Number(e.target.value) / 100 })}
          />
        </label>
        <label className="radio-range">
          Sprechtempo <output>{p.rate.toFixed(2)}×</output>
          <input
            aria-label="Sprechtempo"
            type="range"
            min="0.8"
            max="1.3"
            step="0.05"
            value={p.rate}
            onChange={(e) => audio.change({ rate: Number(e.target.value) })}
          />
        </label>
        <label className="radio-voice">
          Stimmenverteilung
          <select
            aria-label="Stimmenverteilung"
            value={p.voiceMode}
            onChange={(e) => audio.change({ voiceMode: e.target.value as 'mixed' | 'single' })}
          >
            <option value="mixed">Gemischt · männlich/weiblich</option>
            <option value="single">Eine feste Stimme</option>
          </select>
        </label>
        {neural ? (
          p.voiceMode === 'single' ? (
            <label className="radio-voice">
              Deutsche Stimme
              <select
                aria-label="Neuronale Funkstimme"
                value={p.neuralGender}
                onChange={(e) =>
                  audio.change({ neuralGender: e.target.value as 'male' | 'female' })
                }
              >
                <option value="male">Thorsten · männlich</option>
                <option value="female">Kerstin · weiblich</option>
              </select>
            </label>
          ) : (
            <p className="radio-help">
              Deutsche Frauen- und Männerstimme · feste Stimme je Fahrzeug.
            </p>
          )
        ) : p.voiceMode === 'mixed' ? (
          <>
            <label className="radio-voice">
              Weibliche Stimme
              <select
                aria-label="Weibliche Funkstimme"
                value={p.femaleVoiceURI}
                onChange={(e) => audio.change({ femaleVoiceURI: e.target.value })}
              >
                <option value="">Automatisch (empfohlen)</option>
                {p.femaleVoiceURI && !voices.some((v) => v.voiceURI === p.femaleVoiceURI) && (
                  <option value={p.femaleVoiceURI}>Gespeicherte Stimme nicht verfügbar</option>
                )}
                {voices.map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>
                    {v.name} · {v.localService ? 'lokal' : 'Browserdienst'}
                  </option>
                ))}
              </select>
            </label>
            <label className="radio-voice">
              Männliche Stimme
              <select
                aria-label="Männliche Funkstimme"
                value={p.maleVoiceURI}
                onChange={(e) => audio.change({ maleVoiceURI: e.target.value })}
              >
                <option value="">Automatisch (empfohlen)</option>
                {p.maleVoiceURI && !voices.some((v) => v.voiceURI === p.maleVoiceURI) && (
                  <option value={p.maleVoiceURI}>Gespeicherte Stimme nicht verfügbar</option>
                )}
                {voices.map((v) => (
                  <option key={v.voiceURI} value={v.voiceURI}>
                    {v.name} · {v.localService ? 'lokal' : 'Browserdienst'}
                  </option>
                ))}
              </select>
            </label>
          </>
        ) : (
          <label className="radio-voice">
            Deutsche Stimme
            <select
              aria-label="Deutsche Funkstimme"
              value={p.voiceURI}
              onChange={(e) => audio.change({ voiceURI: e.target.value })}
            >
              <option value="">Automatisch (empfohlen)</option>
              {p.voiceURI && !voices.some((v) => v.voiceURI === p.voiceURI) && (
                <option value={p.voiceURI}>Gespeicherte Stimme nicht verfügbar</option>
              )}
              {voices.map((v) => (
                <option key={v.voiceURI} value={v.voiceURI}>
                  {v.name} · {v.localService ? 'lokal' : 'Browserdienst'}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      <p className="radio-help">
        {neural
          ? 'Sprachausgabe wird auf dem eigenen Server erzeugt.'
          : (p.speechProvider === 'auto'
              ? 'Neuronaler Sprachdienst nicht verfügbar. Browserstimmen übernehmen. '
              : '') + voiceAvailability(voices, p)}
      </p>
      <div className="radio-control-actions">
        <button className="button secondary" onClick={() => void audio.test()}>
          <Volume2 size={16} />
          Sprechprobe · Funkdialog
        </button>
        <button className="button secondary" onClick={audio.stop}>
          <Square size={14} />
          Wiedergabe stoppen
        </button>
        <span>
          {runtime.speaking
            ? 'Funkdurchsage läuft'
            : runtime.unlocked && p.enabled
              ? 'Empfang bereit'
              : p.enabled
                ? 'Ton noch nicht aktiv'
                : 'Stummgeschaltet'}
          {runtime.queued > 0 ? ` · ${runtime.queued} vorgemerkt` : ''}
        </span>
      </div>
      {runtime.speaking && runtime.currentVoice && (
        <p className="radio-help" role="status">
          Spricht gerade: {runtime.currentVoice}
        </p>
      )}
      {runtime.error && (
        <p className="form-error" role="alert">
          {runtime.error}
        </p>
      )}
      <p className="radio-help">
        Automatische Durchsagen bleiben kurz. Überholte Meldungen werden ausgelassen; Statusabfragen
        und dringende Lageänderungen kommen als Nächstes. Laufende Funksprüche werden zu Ende
        gesprochen. Das vollständige Funkprotokoll bleibt erhalten. Kräfte vor Ort melden ihren
        Sprechwunsch in wechselnden Abständen. Die Rückmeldung folgt nach deiner Sprechfreigabe.
      </p>
    </section>
  );
}
export default function RadioPanel({
  state,
  act,
  enabled,
  audio,
}: {
  state: SimState;
  act: Perform;
  enabled: boolean;
  audio: RadioController;
}) {
  const [query, setQuery] = useState('');
  const [vehicleId, setVehicleId] = useState(
    state.vehicles.find((v) => v.incidentId)?.id ?? state.vehicles[0]?.id ?? '',
  );
  const [limit, setLimit] = useState(40);
  const candidates = state.vehicles
    .filter((v) => `${v.name} ${v.kind}`.toLowerCase().includes(query.toLowerCase()))
    .slice(0, 100);
  const vehicle = state.vehicles.find((v) => v.id === vehicleId);
  const last = state.radioMessages.findLast(
    (m) => m.kind === 'request' && m.vehicleId === vehicleId,
  );
  const cooling = !!last && state.now - last.at < 10000;
  return (
    <div className="radio-panel">
      <RadioPlaybackStatus audio={audio} />
      <details className="radio-settings">
        <summary>Ton & Sprachausgabe einstellen</summary>
        <RadioAudioControls audio={audio} />
      </details>
      <section className="radio-request-card">
        <div className="radio-section-heading">
          <span>
            <Radio size={18} />
            <b>Status anfordern</b>
          </span>
          <small>Simulierter Funkdialog</small>
        </div>
        <div className="search-field">
          <Search size={15} />
          <input
            aria-label="Fahrzeug für Funkabfrage suchen"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Funkrufname oder Fahrzeugtyp …"
          />
        </div>
        <div className="radio-request-row">
          <select
            aria-label="Fahrzeug für Statusabfrage"
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
          >
            {vehicle && !candidates.some((v) => v.id === vehicleId) && (
              <option value={vehicle.id}>{callsign(vehicle)}</option>
            )}
            {candidates.map((v) => (
              <option key={v.id} value={v.id}>
                {callsign(v)} · Status {v.status}
              </option>
            ))}
          </select>
          <button
            className="button primary"
            disabled={!enabled || !vehicle || cooling}
            onClick={() =>
              void act({ type: 'requestStatus', vehicleId }, 'Statusabfrage im Funkprotokoll.')
            }
          >
            <Radio size={16} />
            {cooling ? 'Antwort liegt vor' : 'Status anfordern'}
          </button>
        </div>
        {vehicle && (
          <p className="radio-help">
            Status {vehicle.status} · {tacticalStatus[vehicle.status]}. Die Abfrage verändert weder
            Status noch Einsatzauftrag.
          </p>
        )}
      </section>
      {audio.runtime.speaking && (
        <div className="radio-now">
          <i className="live-dot" />
          <span>{audio.runtime.currentText}</span>
        </div>
      )}
      <div className="radio-section-heading">
        <span>
          <b>Funkprotokoll</b>
          <small>{state.radioMessages.length} Meldungen</small>
        </span>
        <small>Simulationszeit · neueste zuerst</small>
      </div>
      <div className="radio-history">
        {state.radioMessages
          .slice(-limit)
          .reverse()
          .map((message) => (
            <article className={`radio-message radio-${message.kind}`} key={message.id}>
              <header>
                <time>{time(message.at)}</time>
                <b>{radioKindLabels[message.kind]}</b>
                {message.status && <span>Status {message.status}</span>}
                <button
                  className="radio-replay"
                  disabled={!audio.preferences.speech || !audio.preferences.volume}
                  aria-label={`Funkmeldung ${message.sequence} erneut abspielen`}
                  title="Funkmeldung erneut abspielen"
                  onClick={() => void audio.replay(message)}
                >
                  <Volume2 size={16} />
                </button>
              </header>
              {message.transmissions.map((t, index) => (
                <p key={index}>
                  <strong>{t.sender}</strong>
                  <span>{t.text}</span>
                </p>
              ))}
            </article>
          ))}
        {!state.radioMessages.length && (
          <div className="empty">
            Noch kein Funkverkehr. Fordere einen Fahrzeugstatus an oder alarmiere Kräfte.
          </div>
        )}
      </div>
      {limit < state.radioMessages.length && (
        <button className="button secondary" onClick={() => setLimit((n) => n + 40)}>
          Ältere Meldungen anzeigen
        </button>
      )}
      <p className="radio-help">
        Simulierte BOS-Funkabläufe mit Fahrzeugstatus 1, 2, 3, 4 und 6 sowie Sprechwunsch 5 und
        Freigabe J. Regionale Sprechweisen können abweichen. Automatische Statusansagen sind eine
        zuschaltbare Vorlesefunktion.
      </p>
    </div>
  );
}
